import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { SharedPopupService } from '@bepatient/shared';
import { LoadingController, ToastController, Platform } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { forkJoin, from, Observable, of, timer } from 'rxjs';
import {
  concatMap,
  delay,
  distinctUntilChanged,
  filter,
  map,
  mapTo,
  switchMap,
  take,
  tap
} from 'rxjs/operators';
import {
  CoreHttpActions,
  CoreHttpActionSetPending,
  CoreHttpActionsType,
  CoreHttpActionInternetIsNotWorking,
  CoreHttpActionInternetIsWorking
} from './core-http.actions';
import {
  selectorCoreHttpPending,
  selectorCoreHttpPendingQueue,
  selectorCoreHttpMaintenanceModeMessage
} from './core-http.reducer';
import { CoreUtilsService } from './core-utils';
import { JWTPopupID } from './core.effects';
import {
  selectorCoreInstanceInfo,
  selectorCoreLogin,
  selectorCoreUseGlobalLoading
} from './core.reducer';

const enum NetworkErrorPopupID {
  NO_NETWORK_POPUP = 'Popup Network Error',
  SERVER_RESPONSE_POPUP = 'Popup API Response Error',
  MAINTENANCE_POPUP = 'Popup Maintenance Mode'
}
@Injectable()
export class CoreHttpEffects {
  private store$ = inject<Store<any>>(Store);
  private actions$ = inject(Actions);
  private loadingController = inject(LoadingController);
  private coreUtilsService = inject(CoreUtilsService);
  private sharedPopupService = inject(SharedPopupService);
  private toastController = inject(ToastController);
  private platform = inject(Platform);
  private route = inject(Router);

  private globalLoading: any;

  private checkInternet: boolean;
  private toasts = [];

  checkInternetConnection$: Observable<any> = createEffect(
    () =>
      from(this.platform.ready()).pipe(
        delay(2000),
        tap(() => {
          this.coreUtilsService.networkConnection
            .pipe(distinctUntilChanged())
            .subscribe(isOnline => {
              this.checkInternet = isOnline;
              // checking internet connection
              if (this.checkInternet === true) {
                // Internet is working
                this.store$.dispatch(new CoreHttpActionInternetIsWorking());
              } else {
                // Internet is not working
                this.store$.dispatch(new CoreHttpActionInternetIsNotWorking());
              }
            });
        })
      ),
    { dispatch: false }
  );

  pendingQueueUpdated$: Observable<CoreHttpActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CoreHttpActionsType.DECREMENT_PENDING_QUEUE,
        CoreHttpActionsType.INCREMENT_PENDING_QUEUE
      ),
      concatMap(() => this.store$.pipe(select(selectorCoreHttpPendingQueue))),
      switchMap(pendingQueue =>
        pendingQueue > 0 ? of(true) : timer(200).pipe(mapTo(false))
      ),
      distinctUntilChanged(),
      concatMap(pending => of(new CoreHttpActionSetPending(pending)))
    )
  );

  displayGlobalLoading = createEffect(
    () =>
      this.store$.pipe(
        select(selectorCoreUseGlobalLoading),
        filter(useGlobalLoading => useGlobalLoading),
        concatMap(() => this.store$.pipe(select(selectorCoreHttpPending))),
        concatMap(pending =>
          pending
            ? from(this.setAndPresentGlobalLoading())
            : of(this.globalLoading)
        ),
        tap(loading => loading && loading.dismiss())
      ),
    { dispatch: false }
  );

  displayNetworkErrorPopup = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreHttpActionsType.INTERNET_NOT_WORKING),
        switchMap(() =>
          forkJoin([
            this.coreUtilsService.getSafeTranslations([
              'NO_NETWORK_POPUP.HEADER',
              'NO_NETWORK_POPUP.MESSAGE',
              'NO_NETWORK_POPUP.OK'
            ]),
            this.store$.pipe(select(selectorCoreInstanceInfo), take(1))
          ])
        ),
        map(([translations, instanceInfo]) => {
          if (this.sharedPopupService.exist(JWTPopupID.HAS_EXPIRED)) {
            this.sharedPopupService.dismiss(JWTPopupID.HAS_EXPIRED);
          } else if (this.sharedPopupService.exist(JWTPopupID.WILL_EXPIRE)) {
            this.sharedPopupService.dismiss(JWTPopupID.WILL_EXPIRE);
          } else if (
            this.sharedPopupService.exist(
              NetworkErrorPopupID.SERVER_RESPONSE_POPUP
            )
          ) {
            this.sharedPopupService.dismiss(
              NetworkErrorPopupID.SERVER_RESPONSE_POPUP
            );
          }
          this.sharedPopupService
            .simpleAlert({
              id: NetworkErrorPopupID.NO_NETWORK_POPUP,
              color: instanceInfo && instanceInfo.appColor,
              title: translations[0],
              message: translations[1],
              buttons: [translations[2]],
              isNetworkError: translations[1]
            })
            .then(index => {
              /**
               * In case, making the api request after no network toast,
               * multiple toasts overlaps, so check and remove previous toast before adding new toast.
               */
              if (this.toasts.length > 0) {
                this.toastController.dismiss();
              }
              if (index == 0) {
                this.toasts.push(translations[1]);
              }
            });
        })
      ),
    { dispatch: false }
  );

  hideNetworkErrorToaster = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreHttpActionsType.INTERNET_IS_WORKING),
        filter(
          () =>
            (this.toasts.length > 0 &&
              !this.sharedPopupService.exist(
                NetworkErrorPopupID.NO_NETWORK_POPUP
              )) ||
            this.sharedPopupService.exist(NetworkErrorPopupID.NO_NETWORK_POPUP)
        ),
        switchMap(() => this.store$.pipe(select(selectorCoreLogin), take(1))),
        map(coreLogin => {
          // If user is logged in and current route is '/', reload the page
          // Also, if user is not logged in reload the page
          // Stay in current page when user is logged in and current route is not '/'.
          if ((coreLogin && this.route.url == '/') || !coreLogin) {
            window.location.reload();
          }
          if (
            this.sharedPopupService.exist(NetworkErrorPopupID.NO_NETWORK_POPUP)
          ) {
            this.sharedPopupService.dismiss(
              NetworkErrorPopupID.NO_NETWORK_POPUP
            );
          }
          this.toastController.getTop().then(toast => {
            // If toast is present then dismiss it
            if (toast) {
              this.toastController.dismiss();
            }
          });
          this.toasts.length > 0 ? (this.toasts = []) : void 0;
        })
      ),
    { dispatch: false }
  );

  displayApiResponseErrorPopup = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreHttpActionsType.API_RESPONSE_ERROR),
        switchMap(() =>
          forkJoin([
            this.coreUtilsService.getSafeTranslations([
              'SERVER_ERROR_POPUP.HEADER',
              'SERVER_ERROR_POPUP.MESSAGE',
              'SERVER_ERROR_POPUP.OK'
            ]),
            this.store$.pipe(select(selectorCoreInstanceInfo), take(1)),
            this.store$.pipe(select(selectorCoreLogin), take(1))
          ])
        ),
        map(([translations, instanceInfo, login]) => {
          if (this.sharedPopupService.exist(JWTPopupID.HAS_EXPIRED)) {
            this.sharedPopupService.dismiss(JWTPopupID.HAS_EXPIRED);
          } else if (this.sharedPopupService.exist(JWTPopupID.WILL_EXPIRE)) {
            this.sharedPopupService.dismiss(JWTPopupID.WILL_EXPIRE);
          }
          this.sharedPopupService
            .simpleAlert({
              id: NetworkErrorPopupID.SERVER_RESPONSE_POPUP,
              color: instanceInfo && instanceInfo.appColor,
              title: translations[0],
              message: translations[1],
              buttons: [translations[2]]
            })
            .then(index => {
              index === 0 && !login ? window.location.reload() : void 0;
            });
        })
      ),
    { dispatch: false }
  );

  displayMaintenanceModePopup = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreHttpActionsType.SET_MAINTENANCE_MODE),
        switchMap(() =>
          forkJoin([
            this.coreUtilsService.getSafeTranslations([
              'MAINTENANCE_MODE_POPUP.TITLE',
              'MAINTENANCE_MODE_POPUP.MESSAGE'
            ]),
            this.store$.pipe(select(selectorCoreInstanceInfo), take(1)),
            this.store$.pipe(
              select(selectorCoreHttpMaintenanceModeMessage),
              take(1)
            )
          ])
        ),
        map(([translations, instanceInfo, maintenanceMessage]) => {
          if (this.sharedPopupService.exist(JWTPopupID.HAS_EXPIRED)) {
            this.sharedPopupService.dismiss(JWTPopupID.HAS_EXPIRED);
          } else if (this.sharedPopupService.exist(JWTPopupID.WILL_EXPIRE)) {
            this.sharedPopupService.dismiss(JWTPopupID.WILL_EXPIRE);
          }
          this.sharedPopupService.simpleAlert({
            id: NetworkErrorPopupID.MAINTENANCE_POPUP,
            color: instanceInfo && instanceInfo.appColor,
            // alertClass: 'bepatient-maintenance-alert',
            title: translations[0],
            message: maintenanceMessage || translations[1]
          });
        })
      ),
    { dispatch: false }
  );

  displayInternalError = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreHttpActionsType.INTERNAL_SERVER_ERROR),
        switchMap(() =>
          forkJoin([
            this.coreUtilsService.getSafeTranslations([
              'SERVER_ERROR_POPUP_500.HEADER',
              'SERVER_ERROR_POPUP_500.MESSAGE',
              'SERVER_ERROR_POPUP_500.OK'
            ]),
            this.store$.pipe(select(selectorCoreInstanceInfo), take(1)),
            this.store$.pipe(select(selectorCoreLogin), take(1))
          ])
        ),
        map(([translations, instanceInfo, login]) => {
          if (this.sharedPopupService.exist(JWTPopupID.HAS_EXPIRED)) {
            this.sharedPopupService.dismiss(JWTPopupID.HAS_EXPIRED);
          } else if (this.sharedPopupService.exist(JWTPopupID.WILL_EXPIRE)) {
            this.sharedPopupService.dismiss(JWTPopupID.WILL_EXPIRE);
          }
          this.sharedPopupService
            .simpleAlert({
              id: NetworkErrorPopupID.SERVER_RESPONSE_POPUP,
              color: instanceInfo && instanceInfo.appColor,
              title: translations[0],
              message: translations[1],
              buttons: [translations[2]]
            })
            .then(index => {
              index === 0 && !login ? window.location.reload() : void 0;
            });
        })
      ),
    { dispatch: false }
  );

  private async setAndPresentGlobalLoading() {
    this.globalLoading = await this.loadingController.create({
      spinner: 'circles',
      cssClass: 'custom-global-loading'
    });
    return await this.globalLoading.present();
  }
}
